/* eslint-disable react/prop-types */
/* eslint-disable react/no-multi-comp */
import { PropTypes } from 'prop-types';
import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import Tooltip from '@material-ui/core/Tooltip';
import DescriptionIcon from '@material-ui/icons/Description';
import MoreVert from '@material-ui/icons/MoreVert';

import AccessLevelService, { unverified, guest, free } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import BroadcasterNotesStore from 'stores/BroadcasterNotesStore';
import FirebaseStore from 'stores/FirebaseStore';
import ContextualMenu from 'pages/ProfileV2/ProfileV2Desktop/Broadcast/Chat/UserListTab/ContextualMenu/ContextualMenu';
import { isAnyTypeOfGold } from 'pages/User/AccessLevel';
import useContextualMenu from 'hooks/useContextualMenu';

import defaultImage from '../../../images/header/default_Unknown.60x60.png';

import ImageIcon from './components/ImageIcon';
import Avatar from './components/Avatar';
import styles from './ChatMessageTypes.css';


const MessageContext = React.createContext();
MessageContext.displayName = 'MessageContext';

export function useMessageContext() {
  const context = React.useContext(MessageContext);

  if (!context) {
    throw new Error('useMessageContext must be used within message context');
  }

  return context;
}

/**
 *  Message Context Provider abstract logic for message types components
 */
function Message({
  accessLevel,
  actions,
  children,
  embedProfileV2,
  isBroadcasterChat,
  isDesktop,
  isFanclubSubscribed,
  isTopTipper,
  isTranslated,
  message,
  setMenuClosed,
  setMenuOpened,
}) {
  const value = React.useMemo(
    () => {
      return {
        accessLevel,
        actions,
        embedProfileV2,
        isBroadcasterChat,
        isDesktop,
        isFanclubSubscribed,
        isTopTipper,
        isTranslated,
        message,
        setMenuClosed,
        setMenuOpened,
      };
    },
    [
      accessLevel,
      actions,
      embedProfileV2,
      isBroadcasterChat,
      isDesktop,
      isFanclubSubscribed,
      isTopTipper,
      isTranslated,
      message,
      setMenuClosed,
      setMenuOpened,
    ]
  );

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
}

Message.propTypes = {
  children: PropTypes.node.isRequired,
  message: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isFanclubSubscribed: PropTypes.bool,
  isTopTipper: PropTypes.bool,
  isTranslated: PropTypes.bool,
};

Message.defaultProps = {
  embedProfileV2: false,
  isDesktop: false,
  isFanclubSubscribed: false,
  isTopTipper: false,
  isTranslated: false,
};

function GoldUserView(props) {
  const context = useMessageContext();
  const isGold = isAnyTypeOfGold(context.message.senderAccessLevel);

  return isGold ? props.children : null;
}

function FanclubSubscribedView(props) {
  const context = useMessageContext();
  const isGold = isAnyTypeOfGold(context.message.senderAccessLevel);

  return (context.isFanclubSubscribed && !isGold) ? props.children : null;
}

function BroadcasterView(props) {
  const context = useMessageContext();

  return context.isBroadcasterChat ? props.children : null;
}

/**
 * Abstracted react compound components
 */

function externalUserAvatar() {
  return (
    <div className={styles.profilePicWrapper}>
      <img alt="externalAvatar" className={styles.profilePicture} src={defaultImage} />
    </div>
  );
}

function UserAvatar(props) {
  const context = useMessageContext();

  const username = context.message.senderOriginalUsername;
  const isExternalBroadcaster = context.message.isExternalMessage;

  return isExternalBroadcaster ? externalUserAvatar() : <Avatar username={username} {...props} />;
}

function FanclubUserIcon() {
  const context = useMessageContext();
  if (!context.isBroadcasterChat) {
    return <ImageIcon type="fanclub" />;
  }

  return (
    <Tooltip
      arrow={true}
      placement="bottom"
      title={translate('profile.profile.fanclub.member')}
    >
      <span>
        <ImageIcon type="fanclub" />
      </span>
    </Tooltip>
  );
}

function GoldUserIcon() {
  const context = useMessageContext();
  const showFanclub = context.isFanclubSubscribed;

  const goldBadge = context.isBroadcasterChat ? (
    <Tooltip
      arrow={true}
      placement="bottom"
      title={translate('v2broadcast.gold.user')}
    >
      <span>
        <ImageIcon type="goldstar" />
      </span>
    </Tooltip>
  ) : (<ImageIcon type="goldstar" />);

  return (
    <>
      {goldBadge}
      {showFanclub && <FanclubUserIcon />}
    </>
  );
}

function TopTipperUserIcon() {
  const context = useMessageContext();
  const topTipperBadge = context.isBroadcasterChat ? (
    <Tooltip
      arrow={true}
      placement="bottom"
      title={translate('v2profile.tip.leader.title')}
    >
      <span>
        <ImageIcon type="crown" />
      </span>
    </Tooltip>
  ) : <ImageIcon type="crown" />;

  return topTipperBadge;
}

function MenuIcon({ openMenu }) {
  return (
    <div
      className={styles.menuIcon}
      role="presentation"
      onClick={openMenu}
    >
      <MoreVert />
    </div>
  );
}

function NotesIcon() {
  const context = useMessageContext();
  const { allNotes, getSingleNote } = BroadcasterNotesStore;
  const username = context.message.senderOriginalUsername;

  const handleNoteModal = useCallback((e) => {
    e.stopPropagation();
    getSingleNote(username);
  }, [getSingleNote, username]);

  return allNotes[username] ? (
    <Tooltip
      placement="top"
      title={translate('v2broadcast.edit.note')}
    >
      <DescriptionIcon
        className={allNotes[username] === 'red' ? styles.redNote : styles.blueNote}
        onClick={handleNoteModal}
      />
    </Tooltip>
  ) : null;
}

function ContextualMenuOpener({ children }) {
  const context = useMessageContext();
  const { showMenu, anchorEl, openMenu, closeMenu } = useContextualMenu();

  const callGuardedAction = ({ accessLevel, event }) => {
    event.stopPropagation();
    const guardedActions = {
      showRegistrationModal: {
        checkAccessLevel: AccessLevelService.is(unverified, accessLevel),
        invoke: () => context.actions.showRegistrationModal(),
      },
      openGuestBillingModal: {
        checkAccessLevel: AccessLevelService.is(guest, accessLevel),
        invoke: () => context.actions.openGuestBillingModal(),
      },
      openMenu: {
        checkAccessLevel: AccessLevelService.isMin(free, accessLevel),
        invoke: () => {
          openMenu(event);
          if (context.isBroadcasterChat) {
            context.setMenuOpened();
          }
        },
      },
    };

    const guardedActionKey = Object.keys(guardedActions).find(
      (guardKey) => guardedActions[guardKey].checkAccessLevel
    );

    guardedActions[guardedActionKey].invoke();
  };

  const handleOpen = (event) => {
    callGuardedAction({
      accessLevel: context.accessLevel,
      event,
    });
  };

  const onClose = useCallback(() => {
    closeMenu();
    if (context.isBroadcasterChat) {
      context.setMenuClosed();
    }
  }, [context, closeMenu]);

  const dontShowContextualMenu = context.embedProfileV2;
  const { sortedUsers } = FirebaseStore;
  const { senderOriginalUsername } = context.message;

  const info = sortedUsers.find(
    (el) => el?.originalUsername?.toLowerCase() === senderOriginalUsername?.toLowerCase()
  );

  return (
    <React.Fragment>
      {children(handleOpen)}
      {AccessLevelService.isMin(free, context.accessLevel) && showMenu && (
        <ContextualMenu
          anchorEl={anchorEl}
          close={onClose}
          isBroadcasterChat={context.isBroadcasterChat}
          isDesktop={context.isDesktop}
          isRoomModerator={info?.isRoomModerator}
          isSilenced={info?.isSilenced}
          listItemUserAccessLevel={context.message.senderAccessLevel}
          message={context.message}
          originalUsername={context.message.senderOriginalUsername}
          showContextualMenu={dontShowContextualMenu ? false : showMenu}
        />
      )}
    </React.Fragment>
  );
}

/**
 * Export boolean based components and react components
 * Their job is to keep things simple and add more flexibility in code
 * Each compound component suppose to abstract only one peace of logic and do one thing
 */
Message.FanclubSubscribedView = FanclubSubscribedView;
Message.UserAvatar = UserAvatar;
Message.GoldIcon = GoldUserIcon;
Message.FanclubUserIcon = FanclubUserIcon;
Message.TopTipperUserIcon = TopTipperUserIcon;
Message.ContextualMenuOpener = ContextualMenuOpener;
Message.BroadcasterView = BroadcasterView;
Message.MenuIcon = MenuIcon;
Message.NotesIcon = observer(NotesIcon);
Message.GoldUserView = GoldUserView;

export default Message;
